import React from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2, X } from "lucide-react";
import RouteMapPanel from "./RouteMapPanel.jsx";
import {
  ensureRouteMapPictureInPictureWindowExpanded,
  resizeRouteMapPictureInPictureWindow,
  resolveRouteMapPictureInPictureScreenSize,
  resolveRouteMapPictureInPictureWindowPlacement,
} from "../utils/routeMapPictureInPictureWindow.js";

function isPortalMountNode(node) {
  return Boolean(node && node.nodeType === 1 && node.ownerDocument);
}

function resolvePictureInPictureWindow(mountNode) {
  return mountNode?.ownerDocument?.defaultView || null;
}

function dispatchPictureInPictureResize(pictureInPictureWindow) {
  if (!pictureInPictureWindow) {
    return;
  }

  pictureInPictureWindow.setTimeout?.(() => {
    try {
      pictureInPictureWindow.dispatchEvent(new pictureInPictureWindow.Event("resize"));
    } catch {
      pictureInPictureWindow.dispatchEvent(new Event("resize"));
    }
  }, 80);
}

function resolvePictureInPictureFullscreenElement(targetDocument) {
  return targetDocument?.fullscreenElement
    || targetDocument?.webkitFullscreenElement
    || targetDocument?.mozFullScreenElement
    || targetDocument?.msFullscreenElement
    || null;
}

async function exitPictureInPictureFullscreen(pictureInPictureWindow) {
  const targetDocument = pictureInPictureWindow?.document || null;

  if (!targetDocument || !resolvePictureInPictureFullscreenElement(targetDocument)) {
    return false;
  }

  const exitFullscreen = targetDocument.exitFullscreen
    || targetDocument.webkitExitFullscreen
    || targetDocument.mozCancelFullScreen
    || targetDocument.msExitFullscreen;

  if (typeof exitFullscreen !== "function") {
    return false;
  }

  try {
    await exitFullscreen.call(targetDocument);
    return true;
  } catch {
    return false;
  }
}

function notifyPictureInPictureFullscreenUnavailable(onWindowExpandedChange, nextExpanded) {
  if (typeof onWindowExpandedChange === "function") {
    onWindowExpandedChange(nextExpanded, { unavailable: true });
  }
}

export default function RouteMapPictureInPicturePanel({
  assignment = null,
  isRouteViewActive = true,
  mountNode = null,
  onClose = null,
  onClientSelect = null,
  onControllerReady = null,
  onMapRenderMetaChange = null,
  onRouteHighlightChange = null,
  onWindowExpandedChange = null,
  renderLegend = true,
  routeAlternativePreview = null,
  routeDistanceLabel = "",
  selectedClientId = null,
  selectionRequest = null,
  stateRef = null,
  title = "Carte routière",
  trip = null,
  windowExpanded = false,
}) {
  const previousPictureInPicturePlacementRef = React.useRef(null);
  const [internalIsPictureInPictureExpanded, setInternalIsPictureInPictureExpanded] = React.useState(false);

  const isPictureInPictureExpanded = typeof onWindowExpandedChange === "function"
    ? windowExpanded
    : internalIsPictureInPictureExpanded;

  const handlePictureInPictureFullscreenToggle = React.useCallback(async () => {
    const activePictureInPictureWindow = resolvePictureInPictureWindow(mountNode);

    const nextExpanded = !isPictureInPictureExpanded;

    if (nextExpanded) {
      previousPictureInPicturePlacementRef.current = resolveRouteMapPictureInPictureWindowPlacement(activePictureInPictureWindow);
      const expanded = await ensureRouteMapPictureInPictureWindowExpanded(activePictureInPictureWindow);
      if (expanded) {
        if (typeof onWindowExpandedChange === "function") {
          onWindowExpandedChange(true, { resizedInPlace: true });
        } else {
          setInternalIsPictureInPictureExpanded(true);
        }
        dispatchPictureInPictureResize(activePictureInPictureWindow);
        return;
      }
    } else {
      const exitedFullscreen = await exitPictureInPictureFullscreen(activePictureInPictureWindow);
      if (exitedFullscreen) {
        if (typeof onWindowExpandedChange === "function") {
          onWindowExpandedChange(false, { resizedInPlace: true });
        } else {
          setInternalIsPictureInPictureExpanded(false);
        }
        dispatchPictureInPictureResize(activePictureInPictureWindow);
        return;
      }
    }

    const canResizeActiveWindow = (
      typeof activePictureInPictureWindow?.resizeBy === "function"
      || typeof activePictureInPictureWindow?.resizeTo === "function"
    );

    if (!canResizeActiveWindow) {
      notifyPictureInPictureFullscreenUnavailable(onWindowExpandedChange, nextExpanded);
      return;
    }

    const previousPlacement = previousPictureInPicturePlacementRef.current;
    const resized = resizeRouteMapPictureInPictureWindow(
      activePictureInPictureWindow,
      nextExpanded
        ? resolveRouteMapPictureInPictureScreenSize(activePictureInPictureWindow)
        : previousPlacement,
      {
        placement: nextExpanded ? null : previousPlacement,
        useScreenOrigin: nextExpanded,
      },
    );

    if (!resized) {
      notifyPictureInPictureFullscreenUnavailable(onWindowExpandedChange, nextExpanded);
      return;
    }

    if (typeof onWindowExpandedChange === "function") {
      onWindowExpandedChange(nextExpanded, { resizedInPlace: true });
    } else {
      setInternalIsPictureInPictureExpanded(nextExpanded);
    }
    dispatchPictureInPictureResize(activePictureInPictureWindow);
  }, [isPictureInPictureExpanded, mountNode, onWindowExpandedChange]);

  if (!isPortalMountNode(mountNode)) {
    return null;
  }

  return createPortal(
    <section className="grid h-screen min-h-0 grid-rows-[auto_minmax(0,1fr)] bg-white text-text">
      <header className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b border-[rgba(148,163,184,0.2)] bg-[rgba(248,250,252,0.96)] px-4 py-3">
        <div className="min-w-0">
          <h1 className="m-0 truncate text-base font-black text-text">
            {title}
          </h1>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handlePictureInPictureFullscreenToggle}
            className="inline-flex min-h-9 items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-3 text-xs font-black text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
            aria-label={isPictureInPictureExpanded ? "Quitter le plein écran" : "Passer la fenêtre en plein écran"}
            title={isPictureInPictureExpanded ? "Restaurer" : "Plein écran"}
          >
            {isPictureInPictureExpanded ? (
              <Minimize2 aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Maximize2 aria-hidden="true" className="h-4 w-4" />
            )}
            <span>{isPictureInPictureExpanded ? "Restaurer" : "Plein écran"}</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="inline-grid min-h-9 min-w-9 place-items-center rounded-full border border-border-subtle bg-white text-content-copy transition hover:border-border-accent-strong hover:text-text"
            aria-label="Fermer la fenêtre"
            title="Fermer"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="min-h-0 p-3">
        {assignment && trip ? (
          <RouteMapPanel
            assignment={assignment}
            fillAvailableHeight
            floatingSize="xlarge"
            isFloating
            isRouteViewActive={isRouteViewActive}
            onClientSelect={onClientSelect}
            onControllerReady={onControllerReady}
            onMapRenderMetaChange={onMapRenderMetaChange}
            onRouteHighlightChange={onRouteHighlightChange}
            renderInline
            renderLegend={renderLegend}
            renderObservability={false}
            routeAlternativePreview={routeAlternativePreview}
            routeDistanceLabel={routeDistanceLabel}
            selectedClientId={selectedClientId}
            selectionRequest={selectionRequest}
            stateRef={stateRef}
            trip={trip}
          />
        ) : (
          <div className="grid h-full min-h-80 place-items-center rounded-[18px] border border-dashed border-border-subtle bg-surface-muted px-6 text-center">
            <div className="max-w-100">
              <p className="m-0 text-sm font-black text-text">Aucune carte active</p>
              <p className="m-0 mt-2 text-sm leading-6 text-content-copy">
                Retourne dans Dispatch et utilise le bouton Détacher depuis une carte flottante active.
              </p>
            </div>
          </div>
        )}
      </main>
    </section>,
    mountNode,
  );
}
